"use client"

import type { CSSProperties, ReactNode } from "react"
import { usePathname } from "next/navigation"
import { analytics } from "@/lib/analytics"
import { useImpressionEvent } from "@/hooks/useImpressionEvent"
import {
  CONTENT_LIST_ID_ATTR,
  contentSlugFromPathname,
  pageFamilyFromPathname,
} from "@/lib/analytics/content-links"

/**
 * Threshold for card grids. A grid can be several viewports tall, so the
 * default (half the element on screen) may never be met; 1% means "the list
 * entered the viewport".
 */
export const LIST_IMPRESSION_THRESHOLD = 0.01

export interface ContentModuleImpressionOptions {
  /** Visible share that counts as seen. Defaults to half. */
  threshold?: number
  /**
   * Which list on the page this is, when one module renders several grids.
   * Rides on the impression as `list_id`, and on each click as `list_id` +
   * `list_position` (read from the `data-list-id` attribute).
   */
  listId?: string
}

/**
 * Container impression for a content module: `cta_surface_shown` with
 * `cta_location: content_<module>`, so the module's `content_link_clicked`
 * rows finally have a denominator.
 */
export function useContentModuleImpression<T extends HTMLElement>(
  module: string,
  count?: number,
  options?: ContentModuleImpressionOptions,
) {
  const pathname = usePathname()
  const pageFamily = pageFamilyFromPathname(pathname)
  return useImpressionEvent<T>(
    `content_${module}`,
    {
      surface: module,
      count,
      slug: contentSlugFromPathname(pathname),
      page_family: pageFamily,
      ...(options?.listId ? { list_id: options.listId } : {}),
    },
    { emit: analytics.ctaSurfaceShown, enabled: pageFamily !== null, threshold: options?.threshold },
  )
}

interface ContentModuleImpressionProps {
  module: string
  /** Links (or cards) inside the module, so CTR can be read per link. */
  count?: number
  threshold?: number
  listId?: string
  className?: string
  style?: CSSProperties
  id?: string
  children: ReactNode
}

/**
 * Wrapper for server-rendered modules: carries the `data-module` attribute the
 * delegated click listener resolves and fires the container impression.
 */
export function ContentModuleImpression({
  module,
  count,
  threshold,
  listId,
  className,
  style,
  id,
  children,
}: ContentModuleImpressionProps) {
  const ref = useContentModuleImpression<HTMLDivElement>(module, count, { threshold, listId })
  const listAttr = listId ? { [CONTENT_LIST_ID_ATTR]: listId } : {}
  return (
    <div ref={ref} data-module={module} {...listAttr} className={className} style={style} id={id}>
      {children}
    </div>
  )
}
